/**
 * Context-menu model for one layer-tree row (components/map/LayerTree.js). Pure: the panel
 * renders `layerMenuItems(entry, ...)` with Radix ContextMenu and calls `runLayerMenuAction`
 * on select, so the dispatch is unit-tested without a DOM.
 */

export const OPACITY_STEPS = [100, 75, 50, 25];

const SURVEY_KINDS = ['electrodes', 'pseudosection'];

/** Layer entries that belong to a stored dataset (electrodes / pseudosection, project dataset layers). */
export const isDatasetEntry = (entry) => !!entry?.datasetId;

/**
 * The "Dataset" section for a dataset-backed layer (`data-testid="dsmenu-<key>"`). Georeference
 * and DEM items only exist for resistivity surveys; Rotate and Topography are disabled until the
 * survey is georeferenced (`entry.georeferenced === false`).
 */
export function datasetMenuItems(entry) {
  const survey = SURVEY_KINDS.includes(entry.datasetKind);
  const placed = entry.georeferenced !== false;
  const needGeoref = placed ? undefined : 'Georeference the survey first';
  const items = [
    { id: 'ds:zoom', key: 'zoom', label: 'Zoom to dataset' },
    { id: 'ds:rename', key: 'rename', label: 'Rename dataset…' },
  ];
  if (survey) {
    items.push(
      { id: 'ds:location', key: 'location', label: 'Edit location (georeference)…' },
      { id: 'ds:rotate', key: 'rotate', label: 'Rotate / set azimuth…', disabled: !placed, title: needGeoref },
      { id: 'ds:topography', key: 'topography', label: 'Topography from DEM…', disabled: !placed, title: needGeoref },
      { id: 'ds:pseudosection', key: 'pseudosection', label: 'Pseudosection' },
    );
  }
  items.push({ id: 'ds:page', key: 'page', label: 'Open dataset page' });
  if (survey) {
    items.push({
      id: 'ds:export', key: 'export', label: 'Export geometry',
      items: [
        { id: 'ds:export:geo', key: 'export-geo', label: '.geo' },
        { id: 'ds:export:trn', key: 'export-trn', label: '.trn (EarthImager)' },
        { id: 'ds:export:xlsx', key: 'export-xlsx', label: '.xlsx' },
      ],
    });
  }
  items.push(
    { id: 'ds:remove', key: 'remove', label: 'Remove from map' },
    { id: 'ds:delete', key: 'delete', label: 'Delete dataset…', danger: true },
  );
  return items;
}

/** Items for the menu; `{ separator: true }` entries are dividers, `items` makes a submenu. */
export function layerMenuItems(entry, { isActive = false } = {}) {
  const layerItems = layerOnlyItems(entry, { isActive });
  if (!isDatasetEntry(entry)) return layerItems;
  return [
    { header: true, id: 'hdr-dataset', label: 'Dataset' },
    ...datasetMenuItems(entry),
    { separator: true, id: 'sep-dataset' },
    { header: true, id: 'hdr-layer', label: 'Layer' },
    ...layerItems,
  ];
}

function layerOnlyItems(entry, { isActive = false } = {}) {
  const items = [
    { id: 'zoom', label: 'Zoom to layer', disabled: !entry.zoomable },
    { id: 'properties', label: 'Properties…', disabled: !entry.styleId },
    { id: 'rename', label: 'Rename', shortcut: 'F2', disabled: !entry.renamable },
    { id: 'duplicate', label: 'Duplicate', disabled: !entry.duplicable },
    {
      id: 'export', label: 'Export', disabled: !entry.exportable,
      items: [
        { id: 'export:geojson', label: 'GeoJSON' },
        { id: 'export:kml', label: 'KML' },
        { id: 'export:csv', label: 'CSV' },
      ],
    },
    { separator: true, id: 'sep-1' },
    { id: 'table', label: 'Open attribute table', disabled: !entry.hasTable },
    { id: 'active', label: isActive ? 'Active layer' : 'Set as active layer', disabled: isActive },
  ];
  if (entry.opacity != null) {
    items.push({
      id: 'opacity', label: 'Opacity',
      items: OPACITY_STEPS.map((v) => ({ id: `opacity:${v}`, label: `${v}%`, checked: Math.round(entry.opacity) === v })),
    });
  }
  items.push(
    { separator: true, id: 'sep-2' },
    { id: 'top', label: 'Move to top' },
    { id: 'bottom', label: 'Move to bottom' },
    { separator: true, id: 'sep-3' },
    { id: 'remove', label: 'Remove', shortcut: 'Del', danger: true, disabled: !entry.removable },
  );
  return items;
}

/**
 * Run one menu item. `actions` is the object from hooks/useLayerTree; `ui` supplies the two
 * things that need panel state: `startRename(key)` and `confirmRemove(key)`.
 */
export function runLayerMenuAction(id, entry, actions, ui = {}) {
  const { key } = entry;
  if (id.startsWith('ds:')) return runDatasetMenuAction(id, entry, ui.datasetActions);
  if (id.startsWith('export:')) return actions.exportAs(key, id.slice(7));
  if (id.startsWith('opacity:')) return actions.setOpacity(key, Number(id.slice(8)));
  switch (id) {
    case 'zoom': return actions.zoomTo(key);
    case 'properties': return actions.openProperties(key);
    case 'rename': return ui.startRename?.(key);
    case 'duplicate': return actions.duplicate(key);
    case 'table': return actions.openTable(key);
    case 'active': return actions.setActive(key);
    case 'top': return actions.move(key, 'top');
    case 'bottom': return actions.move(key, 'bottom');
    case 'remove': return ui.confirmRemove?.(key);
    default: return undefined;
  }
}

/** Run a `ds:*` item on `entry`'s dataset; `da` is hooks/useDatasetMenu's action object. */
export function runDatasetMenuAction(id, entry, da) {
  if (!da || !entry?.datasetId) return undefined;
  const ds = entry.datasetId;
  const name = entry.datasetName || entry.name;
  if (id.startsWith('ds:export:')) return da.exportGeometry(ds, id.slice(10), name);
  switch (id) {
    case 'ds:zoom': return da.zoom(ds);
    case 'ds:rename': return da.rename(ds, name);
    case 'ds:location': return da.editLocation(ds, name);
    case 'ds:rotate': return da.rotate(ds, name);
    case 'ds:topography': return da.topography(ds, name);
    case 'ds:pseudosection': return da.pseudosection(ds, name);
    case 'ds:page': return da.openPage(ds);
    case 'ds:remove': return da.removeFromMap(ds);
    case 'ds:delete': return da.deleteDataset(ds, name);
    default: return undefined;
  }
}
